import React, {useCallback, useEffect, useRef, useState} from 'react';

import {BrowserDatamatrixCodeReader} from '@zxing/browser';
import {BarcodeFormat, DecodeHintType} from '@zxing/library';
import {useTranslation} from 'react-i18next';

import {ReactComponent as SwitchIcon} from 'assets/svg/camera-change.svg';
import {ReactComponent as ClearIcon} from 'assets/svg/cross.svg';
import {Modal} from 'components/ui-kit/Modal';

import {breakpoint} from 'theme';
import {useMatchMedia} from 'utils/custom-hooks';

import {
    CameraContainer,
    Video,
    CameraError,
    CameraPlaceholder,
    CameraOverlay,
    CameraActiveSectionCorners,
    CornerMarker,
    CameraHeader,
    CancelIcon,
    Title,
    Description,
    SuccessCheck,
    ScanCanvas,
    SwitchCameraIcon,
} from './styled';

type Props = {
    open: boolean;
    onClose: () => void;
    onScan?: (code: string) => Promise<void> | void;
    continuous?: boolean;
    scanDelay?: number;
};

const VIDEO_BASE_CONFIG = {
    width: {ideal: 640, max: 1280},
    height: {ideal: 480, max: 720},
    frameRate: {ideal: 10, max: 15},
};

const VISIBLE_SIZE = {width: 196, height: 196};
const CROP_CANVAS_SIZE = 380;
const SCAN_INTERVAL_MS = 150;

const HINTS = new Map();
HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.DATA_MATRIX]);
HINTS.set(DecodeHintType.TRY_HARDER, true);

export const CameraScannerModal: React.FC<Props> = ({
    open,
    onClose,
    onScan,
    continuous = false,
    scanDelay = 1500,
}) => {
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines.cameraScannerModal';
    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${GEN_INFO}.${key}`, vars ?? {});

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const readerRef = useRef<BrowserDatamatrixCodeReader | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const scanGenerationRef = useRef(0);
    const scanLockedRef = useRef(false);

    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<'idle' | 'loading' | 'ready'>('idle');
    const [scanLocked, setScanLocked] = useState(false);
    const [isDevicesLoaded, setIsDevicesLoaded] = useState(false);

    const stopScan = useCallback(() => {
        scanGenerationRef.current++;

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        const video = videoRef.current;
        if (video) {
            const stream = video.srcObject as MediaStream | null;
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
            video.srcObject = null;
        }

        scanLockedRef.current = false;
        setScanLocked(false);
        setStatus('idle');
    }, []);

    const initDevices = useCallback(async () => {
        if (isDevicesLoaded) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({video: true});
            const allDevices = await navigator.mediaDevices.enumerateDevices();
            stream.getTracks().forEach((t) => t.stop());

            const videoInputs = allDevices.filter((d) => d.kind === 'videoinput');

            const uniqueDevices = videoInputs.filter(
                (device, index, self) => index === self.findIndex((d) => d.label === device.label),
            );

            setDevices(uniqueDevices);

            if (uniqueDevices.length > 0) {
                const back = uniqueDevices.find((d) => /back|rear/i.test(d.label.toLowerCase()));
                setSelectedDeviceId(
                    back ? back.deviceId : uniqueDevices[uniqueDevices.length - 1].deviceId,
                );
            }
            setIsDevicesLoaded(true);
        } catch (err) {
            console.error('Init error:', err);
            setError(t('error'));
        }
    }, [isDevicesLoaded]);

    const handleSuccess = useCallback(
        (text: string) => {
            scanLockedRef.current = true;
            setScanLocked(true);

            if (!continuous) {
                stopScan();
                onClose();
                onScan?.(text);
            } else {
                onScan?.(text);
                setTimeout(() => {
                    scanLockedRef.current = false;
                    setScanLocked(false);
                }, scanDelay);
            }
        },
        [continuous, onClose, onScan, scanDelay, stopScan],
    );

    const setupScanner = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);

        const reader = readerRef.current ?? new BrowserDatamatrixCodeReader(HINTS);
        readerRef.current = reader;

        intervalRef.current = setInterval(() => {
            if (scanLockedRef.current || !videoRef.current || !canvasRef.current) return;

            const video = videoRef.current;
            if (video.readyState < video.HAVE_ENOUGH_DATA) return;

            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d', {willReadFrequently: true});
            if (!ctx) return;

            const vw = video.videoWidth;
            const vh = video.videoHeight;
            canvas.width = CROP_CANVAS_SIZE;
            canvas.height = CROP_CANVAS_SIZE;

            const displayHeight = video.clientHeight || 1;
            const cropPx = VISIBLE_SIZE.height * (vh / displayHeight);
            const sx = (vw - cropPx) / 2;
            const sy = (vh - cropPx) / 2;

            //@ts-expect-error
            ctx.drawImage(video, sx, sy, cropPx, cropPx, 0, 0, CROP_CANVAS_SIZE, CROP_CANVAS_SIZE);

            try {
                const result = reader.decodeFromCanvas(canvas);
                if (result && !scanLockedRef.current) {
                    const text = result.getText();
                    handleSuccess(text);
                }
            } catch (e) {
                console.error(e);
            }
        }, SCAN_INTERVAL_MS);
    }, [handleSuccess]);

    const startStreaming = useCallback(
        async (deviceId: string) => {
            if (!videoRef.current) {
                setTimeout(() => startStreaming(deviceId), 50);
                return;
            }

            const generation = ++scanGenerationRef.current;
            setStatus('loading');
            setError(null);

            const constraints = {
                audio: false,
                video: {
                    deviceId: {exact: deviceId},
                    ...VIDEO_BASE_CONFIG,
                },
            };

            try {
                const stream = await navigator.mediaDevices.getUserMedia(constraints);

                if (generation !== scanGenerationRef.current) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }

                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                    setStatus('ready');
                    setupScanner();
                }
            } catch (err) {
                console.error('Stream error:', err);
                setError(`Camera error: ${err.name}`);
                setStatus('idle');
            }
        },
        [setupScanner],
    );

    const switchCamera = () => {
        if (devices.length < 2) return;
        const currentIndex = devices.findIndex((d) => d.deviceId === selectedDeviceId);
        const nextIndex = (currentIndex + 1) % devices.length;

        stopScan();
        setTimeout(() => {
            setSelectedDeviceId(devices[nextIndex].deviceId);
        }, 300);
    };

    useEffect(() => {
        if (open) {
            initDevices();
        } else {
            stopScan();
            setIsDevicesLoaded(false);
            setSelectedDeviceId(null);
        }
    }, [open]);

    useEffect(() => {
        if (open && selectedDeviceId && videoRef.current) {
            startStreaming(selectedDeviceId);
        }
    }, [open, selectedDeviceId]);

    return (
        <Modal
            className="camera-scanner-modal"
            hideCloseButton
            show={open}
            onClose={onClose}
            modalWidth={isMobile ? 1 : 0}
        >
            <CameraContainer>
                <CancelIcon
                    fillColor="white"
                    width={16}
                    height={16}
                    source={ClearIcon}
                    onClick={onClose}
                />

                {devices.length > 1 && (
                    <SwitchCameraIcon
                        width={24}
                        height={24}
                        fillColor="white"
                        source={SwitchIcon}
                        onClick={switchCamera}
                    />
                )}

                <CameraHeader>
                    <Title>{t('title')}</Title>
                    <Description>{t('description')}</Description>
                </CameraHeader>

                <CameraActiveSectionCorners $visibleSize={VISIBLE_SIZE}>
                    <CornerMarker $scanLocked={scanLocked} $position="top-left" />
                    <CornerMarker $scanLocked={scanLocked} $position="top-right" />
                    <CornerMarker $scanLocked={scanLocked} $position="bottom-left" />
                    <CornerMarker $scanLocked={scanLocked} $position="bottom-right" />
                    <SuccessCheck $scanLocked={scanLocked} $visibleSize={VISIBLE_SIZE} />
                </CameraActiveSectionCorners>

                <CameraOverlay $visibleSize={VISIBLE_SIZE} />

                {status === 'loading' && <CameraPlaceholder>{t('loading')}</CameraPlaceholder>}
                {error && <CameraError>{error}</CameraError>}

                <ScanCanvas ref={canvasRef} style={{display: 'none'}} />
                <Video ref={videoRef} playsInline muted autoPlay $visible={status === 'ready'} />
            </CameraContainer>
        </Modal>
    );
};
